import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { useMemo, useState } from "react"
import { FormProvider, useFieldArray, useForm } from "react-hook-form"

import { InventoryUntestedLotsDialog } from "../../../../components"
import { useGetInventoryShelvingOptions, useUntestedLotsValidation } from "../../../../hooks"
import { isLotLocked } from "../../../../utils"
import { TransferType } from "../../constants"
import { useTransferPalletSelection } from "../../hooks/useTransferPalletSelection"
import { useTransferPalletSelectionMissingLotDetailsFill } from "../../hooks/useTransferPalletSelectionMissingLotDetailsFill"
import {
  type InterTransferPalletSelectionFormValues,
  interTransferPalletSelectionFormSchema,
} from "../../schemas/transfer"
import type { TransferFormPallet, TransferFormProductInventory } from "../../types"
import { toInterTransferFormPallets, toInterTransferPalletFormValues } from "../../utils/transfer"

import { InterTransferPalletQuantities } from "./InterTransferPalletQuantities"
import { TransferInventoryHeader } from "./TransferInventoryHeader"
import { TransferPalletSelection } from "./TransferPalletSelection"

import { Button } from "@/components/common/Button"
import CancelDialog from "@/components/common/Modals/CancelDialog"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import type { InventoryPalletGroup, InventoryPalletLot, WarehouseArea } from "@/graphql/codegen/graphql"

interface InterTransferPalletSelectionDialogProps {
  inventories: TransferFormProductInventory[]
  productId: number
  warehouseArea: WarehouseArea
  targetWarehouseArea: WarehouseArea
  onClose: () => void
  onSubmit: (pallets: TransferFormPallet[]) => void
}

export const InterTransferPalletSelectionDialog: FC<InterTransferPalletSelectionDialogProps> = ({
  inventories,
  productId,
  warehouseArea,
  targetWarehouseArea,
  onClose,
  onSubmit,
}) => {
  const inventory = inventories.find((item) => item.product.productId === productId)!

  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)

  const { product } = inventory
  const uomName = product.packageUsWeightUomDetails?.name ?? ""
  const form = useForm<InterTransferPalletSelectionFormValues>({
    resolver: zodResolver(interTransferPalletSelectionFormSchema),
    defaultValues: {
      pallets: inventory.pallets.map((pallet) => toInterTransferPalletFormValues(pallet)),
    },
    mode: "onChange",
  })

  const {
    control,
    formState: { isValid, isDirty },
  } = form

  const {
    fields: palletFields,
    append: appendPallet,
    update: updatePallet,
    remove: removePallet,
  } = useFieldArray({ control, name: "pallets" })

  const selectedLots = useMemo(
    () => palletFields.flatMap(({ lots, ...pallet }) => lots.map((lot) => ({ ...lot, pallet }))),
    [palletFields]
  )
  const selectedLotIds = useMemo(() => new Set(selectedLots.map((lot) => lot.rawInventoryId)), [selectedLots])

  const { untestedLots, validateLotsTested, handleUntestedLotsReset } = useUntestedLotsValidation(selectedLots)

  const {
    palletGroups,
    isFetching,
    sortBy,
    sortDirection,
    aisleCodes,
    handleSearchChange,
    handleSortChange,
    handleAisleCodesChange,
  } = useTransferPalletSelection({
    productExternalSystemId: product.externalSystemId!,
    warehouseAreaExternalSystemId: warehouseArea.externalSystemId!,
  })

  useTransferPalletSelectionMissingLotDetailsFill({
    form,
    productExternalSystemId: product.externalSystemId!,
    warehouseAreaExternalSystemId: warehouseArea.externalSystemId!,
  })

  const { aisleOptions } = useGetInventoryShelvingOptions({
    warehouseAreaId: warehouseArea.warehouseAreaId,
    includeUnavailable: true,
  })

  const updatePalletLotSelection = (pallet: InventoryPalletGroup, nextLotIds: Set<string>) => {
    const palletIndex = palletFields.findIndex(({ referenceNbr }) => referenceNbr === pallet.referenceNbr)
    const selectedPallet = form.getValues("pallets")[palletIndex]
    const selectedLotById = new Map(selectedPallet?.lots.map((lot) => [lot.rawInventoryId, lot]))
    const nextLots = pallet.lots
      .filter((lot) => nextLotIds.has(lot.rawInventoryId))
      .map((lot) => selectedLotById.get(lot.rawInventoryId) ?? lot)

    if (!nextLots.length && palletIndex !== -1) {
      removePallet(palletIndex)
      return
    }

    const palletValues = toInterTransferPalletFormValues(selectedPallet ?? pallet, nextLots)

    if (palletIndex === -1) {
      appendPallet(palletValues)
    } else {
      updatePallet(palletIndex, palletValues)
    }
  }

  const handlePalletSelectionChange = (pallet: InventoryPalletGroup) => {
    const selectableLotIds = new Set(pallet.lots.filter((lot) => !isLotLocked(lot)).map((lot) => lot.rawInventoryId))
    const isPalletSelected =
      selectableLotIds.size > 0 && [...selectableLotIds].every((lotId) => selectedLotIds.has(lotId))

    updatePalletLotSelection(pallet, isPalletSelected ? new Set() : selectableLotIds)
  }

  const handleLotSelectionChange = (pallet: InventoryPalletGroup, lot: InventoryPalletLot) => {
    const nextLotIds = new Set(selectedLotIds)

    if (selectedLotIds.has(lot.rawInventoryId)) {
      nextLotIds.delete(lot.rawInventoryId)
    } else {
      nextLotIds.add(lot.rawInventoryId)
    }

    updatePalletLotSelection(pallet, nextLotIds)
  }

  const handleLotRemove = (palletIndex: number, lotIndex: number) => {
    const pallet = palletFields[palletIndex]
    const nextLotIds = new Set(selectedLotIds)

    nextLotIds.delete(pallet.lots[lotIndex].rawInventoryId)
    updatePalletLotSelection(pallet, nextLotIds)
  }

  const handleCancel = () => {
    if (isDirty) {
      setIsCancelConfirmationOpen(true)
    } else {
      onClose()
    }
  }

  const handleSubmit = form.handleSubmit((formValues) => {
    if (!validateLotsTested()) return

    onSubmit(toInterTransferFormPallets(formValues))
  })

  if (isCancelConfirmationOpen) {
    return <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={onClose} />
  }

  if (untestedLots.length) {
    return <InventoryUntestedLotsDialog untestedLots={untestedLots} onClose={handleUntestedLotsReset} />
  }

  return (
    <Dialog
      open
      fullWidth
      maxWidth={false}
      PaperProps={{ className: "h-full" }}
      onClose={handleCancel}
      data-testid='transfer.pallet-selection-dialog'
    >
      <DialogHeader title='Select Pallets' onClose={handleCancel} />
      <TransferInventoryHeader product={product} warehouseArea={warehouseArea} />
      <FormProvider {...form}>
        <DialogContent className='flex min-h-0 flex-1 flex-col gap-6 overflow-hidden px-10 pb-10 pt-6'>
          <TransferPalletSelection
            palletGroups={palletGroups}
            isFetching={isFetching}
            transferType={TransferType.InterWarehouse}
            selectedLotIds={selectedLotIds}
            sortBy={sortBy}
            sortDirection={sortDirection}
            aisleCodes={aisleCodes}
            aisleOptions={aisleOptions}
            uomName={uomName}
            onLotSelectionChange={handleLotSelectionChange}
            onPalletSelectionChange={handlePalletSelectionChange}
            onSortChange={handleSortChange}
            onSearchChange={handleSearchChange}
            onAisleCodesChange={handleAisleCodesChange}
          />
          <InterTransferPalletQuantities
            targetWarehouseArea={targetWarehouseArea}
            palletFields={palletFields}
            uomName={uomName}
            onLotRemove={handleLotRemove}
          />
        </DialogContent>
      </FormProvider>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button
          appearance='outlined'
          onClick={handleCancel}
          data-testid='transfer.pallet-selection-dialog.cancel-button'
        >
          Cancel
        </Button>
        <Button
          appearance='contained'
          disabled={!isValid}
          onClick={handleSubmit}
          data-testid='transfer.pallet-selection-dialog.submit-button'
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  )
}
